import { useState, useEffect, useRef } from 'react';
import { Box } from '@/components/primitives';
import { Input } from '@/components/primitives';
import { changePassword } from '@/api';
import { useAsyncAction } from '@/hooks/useAsyncAction';
import { useFeedbackZone } from '@/components/ui/FeedbackZone';
import { extractApiMessage } from '@/lib/apiError';
import { vars } from '@/theme';
import { StyledDialog, StyledDialogFooterActions } from '../StyledDialog';
import {
  dialogMessages,
  passwordRules,
  passwordConfig,
  validationMessages,
} from '@/constants';
import { testIds } from '@/test/testIds';

export interface ResetPasswordDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  /** Override the dialog title (defaults to dialogMessages.passwordReset.title). */
  title?: string;
}

// Password validation matching backend rules (8-50 chars, lowercase, digit, special)
const validatePasswordInput = (password: string): string[] => {
  const errors: string[] = [];

  // Use centralized password rules
  if (!passwordRules.length.test(password)) {
    errors.push(passwordRules.length.message);
  }
  if (!passwordRules.lowercase.test(password)) {
    errors.push(passwordRules.lowercase.message);
  }
  if (!passwordRules.number.test(password)) {
    errors.push(passwordRules.number.message);
  }
  if (!passwordRules.special.test(password)) {
    errors.push(passwordRules.special.message);
  }

  // Check for 4+ repeated characters (backend rule)
  if (/(.)\1{3,}/.test(password)) {
    errors.push(dialogMessages.passwordReset.repeatedChars);
  }

  // Check for 4+ sequential characters (backend rule)
  const hasSequential = (str: string): boolean => {
    for (let i = 0; i < str.length - 3; i++) {
      const c1 = str.charCodeAt(i);
      const c2 = str.charCodeAt(i + 1);
      const c3 = str.charCodeAt(i + 2);
      const c4 = str.charCodeAt(i + 3);
      // Check ascending sequence
      if (c2 - c1 === 1 && c3 - c2 === 1 && c4 - c3 === 1) return true;
      // Check descending sequence
      if (c1 - c2 === 1 && c2 - c3 === 1 && c3 - c4 === 1) return true;
    }
    return false;
  };

  if (hasSequential(password.toLowerCase())) {
    errors.push(dialogMessages.passwordReset.sequentialChars);
  }

  return errors;
};

/**
 * Reset Password Dialog - Set new password after reset link
 * Uses StyledDialog with clean appearance (minimal chrome, content-focused)
 * Opens automatically when password_reset_required is true
 *
 * Feedback ownership (#821): a dialog component's own `useFeedbackZone()`
 * resolves to its PARENT layer, so this component holds only `pageZone` (the
 * "Password successfully updated." success outlives the dialog) and
 * `ResetPasswordActions` — the footer, rendered inside the StyledDialog —
 * owns the update: its failure lands in this dialog's own band, once.
 */
export function ResetPasswordDialog({
  open,
  onClose,
  onSuccess,
  title,
}: ResetPasswordDialogProps) {
  // Resolved OUTSIDE the StyledDialog → the page zone, which survives close.
  const pageZone = useFeedbackZone();
  // Lifted from the footer: an update in flight locks the form and the close.
  const [loading, setLoading] = useState(false);
  // The footer owns the update (it must render inside the dialog, #821); the
  // form's Enter runs the same action through this handle.
  const submitRef = useRef<(() => void) | null>(null);

  // Sso conditionally mounts this dialog, so a fresh open is a fresh mount
  // and state initializes from scratch. No reset Effect needed.
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordErrors, setPasswordErrors] = useState<string[]>([]);

  // Validate password on change
  useEffect(() => {
    if (newPassword) {
      setPasswordErrors(validatePasswordInput(newPassword));
    } else {
      setPasswordErrors([]);
    }
  }, [newPassword]);

  const passwordsMatch = newPassword === confirmPassword;
  const isValid =
    newPassword.length > 0 &&
    confirmPassword.length > 0 &&
    passwordsMatch &&
    passwordErrors.length === 0;

  const handleUpdated = () => {
    pageZone.push(dialogMessages.passwordReset.success, 'success');
    onSuccess?.();
    onClose();
  };

  const getPasswordHelperText = () => {
    if (passwordErrors.length > 0) {
      return passwordErrors[0];
    }
    return `Must be ${passwordConfig.minLength}-${passwordConfig.maxLength} chars with lowercase letters, numbers, and special characters (${passwordConfig.specialChars})`;
  };

  const getConfirmHelperText = () => {
    if (confirmPassword && !passwordsMatch) {
      return validationMessages.password.mismatch;
    }
    return '';
  };

  return (
    <StyledDialog
      open={open}
      onClose={onClose}
      title={title ?? dialogMessages.passwordReset.title}
      testId={testIds.dialog.setPassword}
      closeDisabled={loading}
      footer={
        <ResetPasswordActions
          input={isValid ? { newPassword, reNewPassword: confirmPassword } : null}
          onUpdated={handleUpdated}
          onCancel={onClose}
          onBusyChange={setLoading}
          submitRef={submitRef}
        />
      }
    >
      <form
        noValidate
        onSubmit={(e) => e.preventDefault()}
        // A form with two fields and no submit button never submits on Enter by
        // itself, so Enter in a field runs the footer's action here.
        onKeyDown={(e) => {
          if (e.key !== 'Enter' || e.target instanceof HTMLButtonElement) return;
          e.preventDefault();
          submitRef.current?.();
        }}
      >
        <Box paddingTop="sm">
          <Input
            label={dialogMessages.passwordReset.newPasswordLabel}
            type="password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            required
            disabled={loading}
            error={passwordErrors.length > 0}
            helperText={getPasswordHelperText()}
            style={{ marginBottom: vars.spacing.md }}
            autoFocus
          />

          <Input
            label={dialogMessages.passwordReset.confirmPasswordLabel}
            type="password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            required
            disabled={loading}
            error={confirmPassword.length > 0 && !passwordsMatch}
            helperText={getConfirmHelperText()}
          />
        </Box>
      </form>
    </StyledDialog>
  );
}

/**
 * The dialog's footer, rendered INSIDE the StyledDialog, so the update's
 * failure reaches this dialog's own band through `useFeedbackZone`.
 */
function ResetPasswordActions({
  input,
  onUpdated,
  onCancel,
  onBusyChange,
  submitRef,
}: {
  /** The change to send, or null while the form is invalid. */
  input: { newPassword: string; reNewPassword: string } | null;
  /** Success: the caller reports it on a zone that survives close, then closes. */
  onUpdated: () => void;
  onCancel: () => void;
  onBusyChange: (busy: boolean) => void;
  /** Filled with the submit action so the form's Enter runs it. */
  submitRef: { current: (() => void) | null };
}) {
  const zone = useFeedbackZone();
  const { loading, execute } = useAsyncAction();

  useEffect(() => {
    onBusyChange(loading);
    // A forced unmount mid-update must not leave the dialog locked.
    return () => onBusyChange(false);
  }, [loading, onBusyChange]);

  const handleSubmit = async () => {
    if (!input) return;
    await execute(() => changePassword(input), {
      showError: false,
      onSuccess: onUpdated,
      // The server's sentence (e.g. a password-policy refusal) says what to
      // change; the friendly copy is for failures that carry none.
      onError: (err) => zone.push(extractApiMessage(err, dialogMessages.passwordReset.failed), 'error'),
    });
  };

  // Enter in a field submits only when the form is valid (a disabled primary
  // does nothing) and no update is already in flight.
  useEffect(() => {
    submitRef.current = input && !loading ? () => void handleSubmit() : null;
    return () => {
      submitRef.current = null;
    };
  });

  return (
    <StyledDialogFooterActions
      footer={{
        variant: 'confirm',
        primaryText: dialogMessages.passwordReset.setPasswordButton,
        secondaryText: dialogMessages.passwordReset.notNowButton,
        onPrimary: handleSubmit,
        loading,
        primaryDisabled: input === null,
      }}
      onClose={onCancel}
    />
  );
}
